/**
 * useGlobePresence — polls the active-earth presence layer, sends a
 * heartbeat every 60s, and exposes
 *   { activeUsers, friendArcs, userHeat, activeCount }
 * for the globe overlay to render.
 *
 * Apr 29 2026: friend-only flight paths + user heat signature +
 * active-earth presence batch. The hook only fires when `enabled` is
 * true (e.g., on /world but NOT /demo where the spinning intro globe
 * doesn't need a social layer).
 *
 * Heartbeat lat/lon comes from /api/geo/detect on first mount, so anon
 * visitors still appear on the active-earth globe at their detected
 * IP location.
 */
import { useEffect, useRef, useState } from 'react';
import api from './api';

const HEARTBEAT_INTERVAL_MS = 60 * 1000;
const POLL_INTERVAL_MS = 30 * 1000;

export default function useGlobePresence({ enabled = true } = {}) {
  const [data, setData] = useState({ activeUsers: [], friendArcs: [], userHeat: [], activeCount: 0 });
  const heartbeatPosRef = useRef(null);
  const aliveRef = useRef(true);

  useEffect(() => {
    aliveRef.current = true;
    if (!enabled) return undefined;

    let pollTimer;
    let heartbeatTimer;

    const sendHeartbeat = async () => {
      const pos = heartbeatPosRef.current;
      if (!pos) return;
      try {
        await api.post('/presence/heartbeat', {
          lat: pos.lat,
          lon: pos.lon,
          country: pos.country || null,
          city: pos.city || null,
        });
      } catch { /* silent — heartbeat is fire-and-forget */ }
    };

    const fetchSocial = async () => {
      try {
        const r = await api.get('/globe/social');
        if (!aliveRef.current) return;
        setData({
          activeUsers: r.data?.active_users || [],
          friendArcs: r.data?.friend_arcs || [],
          userHeat: r.data?.user_heat || [],
          activeCount: r.data?.active_count || 0,
        });
      } catch { /* silent — empty state is fine */ }
    };

    // 1. One-shot geo detect for the heartbeat position. We don't ask
    // the browser for navigator.geolocation — that would prompt a
    // permission dialog the demo doesn't need. IP-detect is "good
    // enough" granularity for an active-earth dot.
    api.get('/geo/detect')
      .then((r) => {
        if (!aliveRef.current) return;
        const lat = r.data?.lat ?? r.data?.detected?.lat;
        const lon = r.data?.lon ?? r.data?.detected?.lon;
        const country = r.data?.country?.iso2 || r.data?.detected?.country_code || null;
        const city = r.data?.city?.name || r.data?.detected?.city || null;
        if (typeof lat === 'number' && typeof lon === 'number') {
          heartbeatPosRef.current = { lat, lon, country, city };
          sendHeartbeat();
        }
      })
      .catch(() => { /* ignore — empty heartbeat is fine */ });

    // 2. Poll loops.
    fetchSocial();
    pollTimer = setInterval(fetchSocial, POLL_INTERVAL_MS);
    heartbeatTimer = setInterval(sendHeartbeat, HEARTBEAT_INTERVAL_MS);

    return () => {
      aliveRef.current = false;
      clearInterval(pollTimer);
      clearInterval(heartbeatTimer);
    };
  }, [enabled]);

  return data;
}
